/**
 * BESTIX v5.0 — Unified Search Component
 * Search across events, venues, and performers with grouped dropdown results
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Users, Calendar, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import type { Event, VenueSearchResult, PerformerSearchResult } from '@/lib/api';

// ============================================
// Types
// ============================================

interface UnifiedSearchProps {
  placeholder?: string;
  label?: string;
  className?: string;
  variant?: 'hero' | 'header';
}

interface SearchResults {
  events: Event[];
  venues: VenueSearchResult[];
  performers: PerformerSearchResult[];
  isLoading: boolean;
}

// ============================================
// Debounce Hook
// ============================================

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

// ============================================
// Unified Search Component
// ============================================

export function UnifiedSearch({
  placeholder = 'Search events, artists, venues...',
  label = 'WHERE WOULD YOU LIKE TO GO?',
  className,
  variant = 'hero',
}: UnifiedSearchProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResults>({
    events: [],
    venues: [],
    performers: [],
    isLoading: false,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebounce(query, 300);

  // Search all endpoints
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setResults({ events: [], venues: [], performers: [], isLoading: false });
      return;
    }

    setResults(prev => ({ ...prev, isLoading: true }));

    try {
      const [eventsRes, venuesRes, performersRes] = await Promise.all([
        api.events.search({ q: searchQuery, per_page: 5 }).catch(() => ({ events: [] })),
        api.events.searchVenues(searchQuery).catch(() => ({ venues: [] })),
        api.events.searchPerformers(searchQuery).catch(() => ({ performers: [] })),
      ]);

      setResults({
        events: eventsRes.events || [],
        venues: venuesRes.venues || [],
        performers: performersRes.performers || [],
        isLoading: false,
      });
    } catch (error) {
      console.error('Search error:', error);
      setResults({ events: [], venues: [], performers: [], isLoading: false });
    }
  }, []);

  // Trigger search on debounced query change
  useEffect(() => {
    performSearch(debouncedQuery);
  }, [debouncedQuery, performSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle form submission (search all events)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/events?q=${encodeURIComponent(query)}`);
      setIsOpen(false);
    }
  };

  // Navigation handlers
  const handleEventClick = (eventId: number) => {
    navigate(`/events/${eventId}`);
    setIsOpen(false);
    setQuery('');
  };

  const handleVenueClick = (venueId: number) => {
    navigate(`/events?venue_id=${venueId}`);
    setIsOpen(false);
    setQuery('');
  };

  const handlePerformerClick = (performerId: number) => {
    navigate(`/events?performer_id=${performerId}`);
    setIsOpen(false);
    setQuery('');
  };

  const hasResults = results.events.length > 0 || results.venues.length > 0 || results.performers.length > 0;
  const showDropdown = isOpen && (hasResults || results.isLoading || (query.length >= 2 && !results.isLoading));

  // Hero variant styles
  if (variant === 'hero') {
    return (
      <div ref={containerRef} className={cn('relative max-w-2xl mx-auto', className)}>
        <form onSubmit={handleSubmit}>
          <div className="frosted-glass rounded-lg p-1">
            <label className="block text-[10px] text-white/80 uppercase tracking-widest pt-4 pb-2 px-5">
              {label}
            </label>
            <div className="flex items-center bg-white/10 rounded-md mx-2 mb-2">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                placeholder={placeholder}
                className="flex-1 bg-transparent text-white placeholder-white/50 px-5 py-4 text-lg outline-none"
                autoComplete="off"
              />
              {results.isLoading ? (
                <div className="p-4 text-white/80">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : (
                <button type="submit" className="p-4 text-white/80 hover:text-white transition-colors">
                  <Search className="w-6 h-6" />
                </button>
              )}
            </div>
          </div>
        </form>

        {/* Dropdown Results */}
        {showDropdown && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-2xl overflow-hidden z-50 max-h-[60vh] overflow-y-auto">
            {results.isLoading && (
              <div className="p-4 text-center text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Searching...
              </div>
            )}

            {!results.isLoading && !hasResults && query.length >= 2 && (
              <div className="p-4 text-center text-gray-500">
                No results found for "{query}"
              </div>
            )}

            {/* Events */}
            {results.events.length > 0 && (
              <div className="border-b border-gray-100 last:border-0">
                <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" />
                  Events
                </div>
                {results.events.map((event) => (
                  <button
                    key={event.id}
                    onClick={() => handleEventClick(event.id)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-start gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{event.name}</div>
                      <div className="text-sm text-gray-500 truncate">
                        {event.venue?.name} • {event.date}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Venues */}
            {results.venues.length > 0 && (
              <div className="border-b border-gray-100 last:border-0">
                <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" />
                  Venues
                </div>
                {results.venues.map((venue) => (
                  <button
                    key={venue.id}
                    onClick={() => handleVenueClick(venue.id)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{venue.name}</div>
                    <div className="text-sm text-gray-500">
                      {[venue.city, venue.state].filter(Boolean).join(', ')}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Performers */}
            {results.performers.length > 0 && (
              <div className="border-b border-gray-100 last:border-0">
                <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                  <Users className="w-3.5 h-3.5" />
                  Artists & Teams
                </div>
                {results.performers.map((performer) => (
                  <button
                    key={performer.id}
                    onClick={() => handlePerformerClick(performer.id)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{performer.name}</div>
                    {performer.category && (
                      <div className="text-sm text-gray-500">{performer.category}</div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* View all results */}
            {hasResults && (
              <button
                onClick={handleSubmit}
                className="w-full px-4 py-3 text-center text-sm font-medium text-[var(--color-gold)] hover:bg-gray-50 transition-colors"
              >
                View all results for "{query}"
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Header variant (compact)
  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <form onSubmit={handleSubmit} className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-gold)] focus:border-transparent"
          autoComplete="off"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        {results.isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </form>

      {/* Dropdown - same as hero but styled for header */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50 max-h-[400px] overflow-y-auto">
          {/* Same content as hero dropdown */}
          {results.isLoading && (
            <div className="p-3 text-center text-gray-500 text-sm">Searching...</div>
          )}

          {!results.isLoading && !hasResults && query.length >= 2 && (
            <div className="p-3 text-center text-gray-500 text-sm">No results found</div>
          )}

          {results.events.length > 0 && (
            <div className="border-b border-gray-100 last:border-0">
              <div className="px-3 py-1.5 bg-gray-50 text-xs font-semibold text-gray-500 uppercase">Events</div>
              {results.events.slice(0, 3).map((event) => (
                <button
                  key={event.id}
                  onClick={() => handleEventClick(event.id)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors text-sm"
                >
                  <div className="font-medium text-gray-900 truncate">{event.name}</div>
                </button>
              ))}
            </div>
          )}

          {results.venues.length > 0 && (
            <div className="border-b border-gray-100 last:border-0">
              <div className="px-3 py-1.5 bg-gray-50 text-xs font-semibold text-gray-500 uppercase">Venues</div>
              {results.venues.slice(0, 3).map((venue) => (
                <button
                  key={venue.id}
                  onClick={() => handleVenueClick(venue.id)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors text-sm"
                >
                  <div className="font-medium text-gray-900">{venue.name}</div>
                </button>
              ))}
            </div>
          )}

          {results.performers.length > 0 && (
            <div className="border-b border-gray-100 last:border-0">
              <div className="px-3 py-1.5 bg-gray-50 text-xs font-semibold text-gray-500 uppercase">Artists</div>
              {results.performers.slice(0, 3).map((performer) => (
                <button
                  key={performer.id}
                  onClick={() => handlePerformerClick(performer.id)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors text-sm"
                >
                  <div className="font-medium text-gray-900">{performer.name}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default UnifiedSearch;
